import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BoutonCat } from '../components/BoutonCat.js';
import Quiz from './Quiz.js';
import quizService from '../api/quizService.js';
import { useAuth } from '../hooks/useAuth.js';
import { BestScore } from '../components/BestScore.js';
import { setCookie } from '../utils/cookieUtils.js';
import '../styles/Main.css';

export function Main() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [CATEGORIE_SELECTIONNEE, SETCATEGORIE_SELECTIONNEE] = useState(null);
    const [CATEGORIES, SETCATEGORIE] = useState([]);
    const [STATS, SETSTATS] = useState({});
    const [LOADING, SETLOADING] = useState(true);
    const [ERROR, SETERROR] = useState(null);
    const [SHOW_QUIZ, SETSHOW_QUIZ] = useState(false);

    const GET_CATEGORY_ID = (cat) => cat.idCat ?? cat.idcat;

    // Récupérer toutes les catégories
    useEffect(() => {
        const fetchCategories = async () => {
            try {
                SETLOADING(true);
                const DATA = await quizService.getCategories();
                SETCATEGORIE(DATA);
                SETERROR(null);
            } catch (err) {
                console.error('Erreur lors de la récupération des catégories:', err);
                SETERROR('Impossible de charger les catégories');
            } finally {
                SETLOADING(false);
            }
        };

        fetchCategories();
    }, []);

    // Récupérer les statistiques de l'utilisateur
    const fetchStats = async () => {
        if (user && user.idCompte) {
            try {
                const statsData = await quizService.getStats(user.idCompte);
                // Créer un objet pour accéder facilement aux stats par catégorie
                const statsMap = {};
                if (statsData.parCategorie) {
                    statsData.parCategorie.forEach((stat) => {
                        const catId = stat.idCat || stat.idcat;
                        statsMap[catId] = stat;
                    });
                }
                SETSTATS(statsMap);
            } catch (err) {
                console.error('Erreur lors de la récupération des statistiques:', err);
            }
        }
    };

    useEffect(() => {
        fetchStats();
    }, [user]);

    useEffect(() => {
        if (!user) {
            navigate('/login');
        }
    }, [user, navigate]);

    const HANDLE_LOGOUT = () => {
        logout();
        navigate('/login');
    };

    const HANDLE_CATEGORIES_CLICK = (id) => {
        const selectedCategory = CATEGORIES.find((cat) => GET_CATEGORY_ID(cat) === id);
        if (selectedCategory && selectedCategory.diminutif) {
            setCookie('lastSelectedCategory', selectedCategory.diminutif);
        }
        SETCATEGORIE_SELECTIONNEE(id);
        SETSHOW_QUIZ(true);
    };

    const HANDLE_EXIT_QUIZ = () => {
        SETSHOW_QUIZ(false);
        SETCATEGORIE_SELECTIONNEE(null);
        // Rafraîchir les stats après un quiz
        setTimeout(() => {
            fetchStats();
        }, 500);
    };

    // Si le quiz doit être affiché
    if (SHOW_QUIZ && CATEGORIE_SELECTIONNEE) {
        const selectedCategory = CATEGORIES.find((cat) => GET_CATEGORY_ID(cat) === CATEGORIE_SELECTIONNEE);
        return <Quiz categoryId={CATEGORIE_SELECTIONNEE} category={selectedCategory?.diminutif} onExit={HANDLE_EXIT_QUIZ} />;
    }

    const BEST_STAT = Object.values(STATS).reduce((best, current) => {
        if (!current) {
            return best;
        }
        const currentScore = Number(current.meilleurScore ?? current.meilleurscore ?? current.bestScore ?? 0);
        const bestScore = Number(best?.meilleurScore ?? best?.meilleurscore ?? best?.bestScore ?? 0);
        return currentScore > bestScore ? current : best;
    }, null);

    const BEST_SCORE_VALUE = Number(
        BEST_STAT?.meilleurScore ?? BEST_STAT?.meilleurscore ?? BEST_STAT?.bestScore ?? 0
    );

    const BEST_CATEGORY_LABEL =
        BEST_STAT?.libelleCategorie ??
        BEST_STAT?.libellecategorie ??
        CATEGORIES.find((cat) => GET_CATEGORY_ID(cat) === (BEST_STAT?.idCat ?? BEST_STAT?.idcat))?.libelleCategorie ??
        CATEGORIES.find((cat) => GET_CATEGORY_ID(cat) === (BEST_STAT?.idCat ?? BEST_STAT?.idcat))?.libellecategorie ??
        '-';
    
    if (LOADING) {
        return (
            <section className="main-container">
                <div className="main-title">Choisir une catégorie</div>
                <div className="loading-message">Chargement des catégories...</div>
            </section>
        );
    }
    
    if (ERROR) {
        return (
            <div className="main-container">
                <div className="main-title">Choisir une catégorie</div>
                <div style={{ color: 'yellow' }}>{ERROR}</div>
            </div>
        );
    }
    
    return (
        <div className="main-container">

            <div className="logo">
                    <img src="/logo.png" alt="Culture Quiz Logo" />
                </div>
            <BestScore bestCat={BEST_CATEGORY_LABEL} bestScore={BEST_SCORE_VALUE} />
            
            <div className="choice-category">
                <div className="main-title">Choisir une catégorie</div>
                <div className="categories-grid">
                    {CATEGORIES.map((cat) => {
                        const catId = GET_CATEGORY_ID(cat);
                        const catStats = STATS[catId] || {};
                        return (
                            <BoutonCat
                                key={catId}
                                category={cat.diminutif}
                                score={catStats.meilleurScore || catStats.meilleurscore || catStats.bestScore || 0}
                                nbQuestion={catStats.totalQuestionsRepondues || catStats.totalquestionsrepondues || catStats.totalQuestions || 0}
                                onClick={() => HANDLE_CATEGORIES_CLICK(catId)}
                                isActive={CATEGORIE_SELECTIONNEE === catId}
                            />
                        );
                    })}
                </div>
            </div>

            <button className="logout-button" type="button" onClick={HANDLE_LOGOUT}>
                Deconnexion
            </button>
    </div>
    );
}